/**
 * Share modal: mint a signed expiring URL for a buffered session, list
 * existing tokens, revoke, copy.
 *
 * Token is shown once on creation (the only time the raw token leaves
 * the server). Subsequent listings only show metadata.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Copy, ExternalLink, Share2, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import api from '../services/api';
import { ShareToken } from '../types';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  sessionId: string;
  cameraId?: string | null;
  label?: string;
}

const EXPIRY_OPTIONS: { label: string; seconds: number }[] = [
  { label: '1 hour', seconds: 3600 },
  { label: '24 hours', seconds: 86400 },
  { label: '7 days', seconds: 7 * 86400 },
  { label: '30 days', seconds: 30 * 86400 },
];

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function buildShareUrl(token?: string): string {
  if (!token) return '';
  const origin = window.location.origin;
  return `${origin}/share/${token}/viewer`;
}

function buildRawM3u8Url(token?: string): string {
  if (!token) return '';
  return `${window.location.origin}/share/${token}/v2/master.m3u8`;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  deviceId,
  sessionId,
  cameraId,
  label,
}) => {
  const [expirySec, setExpirySec] = useState<number>(86400);
  const [labelInput, setLabelInput] = useState<string>(label || '');
  const [tokens, setTokens] = useState<ShareToken[]>([]);
  const [busy, setBusy] = useState(false);
  const [latestUrl, setLatestUrl] = useState<string | null>(null);
  const [latestRawUrl, setLatestRawUrl] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const list = await api.listShareLinks(deviceId, sessionId);
      setTokens(list);
    } catch {
      setTokens([]);
    }
  }, [deviceId, sessionId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const issue = async () => {
    setBusy(true);
    setLatestUrl(null);
    try {
      const tok = await api.createShareLink(deviceId, sessionId, {
        expires_in_seconds: expirySec,
        camera_id: cameraId || null,
        label: labelInput || null,
      });
      setLatestUrl(buildShareUrl(tok.token));
      setLatestRawUrl(buildRawM3u8Url(tok.token));
      await refresh();
    } catch (err) {
      console.warn('createShareLink failed', err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: number) => {
    setBusy(true);
    try {
      await api.revokeShareLink(id);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-accent">
            <Share2 className="size-5" />
            Share · {sessionId}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-fg-secondary">
          Recipients can play the manifest URL with no login. Token is revocable.{' '}
          {cameraId ? `Scoped to camera ${cameraId}.` : 'Covers all cameras.'}
        </p>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select value={String(expirySec)} onValueChange={(v) => setExpirySec(Number(v))}>
            <SelectTrigger className="sm:w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRY_OPTIONS.map((opt) => (
                <SelectItem key={opt.seconds} value={String(opt.seconds)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Label (optional)"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            className="flex-1"
          />
          <Button
            variant="outline"
            disabled={busy}
            onClick={issue}
            className="border-accent text-accent hover:bg-accent/10"
          >
            Mint link
          </Button>
        </div>

        {latestUrl && (
          <div className="space-y-1">
            <div className="flex items-center gap-2 rounded-md border border-accent bg-bg-surface p-2">
              <span className="shrink-0 text-xs text-accent">map player:</span>
              <span className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-sm">
                {latestUrl}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => copy(latestUrl)}
                      aria-label="Copy synced-viewer URL"
                    >
                      <Copy />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy synced-viewer URL</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => window.open(latestUrl, '_blank', 'noopener')}
                      aria-label="Open in new tab"
                    >
                      <ExternalLink />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Open in new tab</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {latestRawUrl && (
              <div className="flex items-center gap-2 pl-2">
                <span className="shrink-0 text-xs text-fg-muted">raw m3u8 (VLC/ffplay):</span>
                <span className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-xs text-fg-secondary">
                  {latestRawUrl}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => copy(latestRawUrl)}
                        className="h-7 w-7"
                        aria-label="Copy raw m3u8"
                      >
                        <Copy className="size-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy raw m3u8</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        )}

        <div className="h-px bg-border" />

        <div className="text-sm font-medium text-accent">Existing tokens</div>

        {busy && tokens.length === 0 ? (
          <Spinner size={20} />
        ) : tokens.length === 0 ? (
          <p className="text-sm text-fg-muted">No share links yet.</p>
        ) : (
          <div className="space-y-2">
            {tokens.map((t) => {
              const stateLabel = t.is_revoked ? 'revoked' : t.is_expired ? 'expired' : 'active';
              const stateClass = t.is_revoked
                ? 'border-fg-muted text-fg-muted'
                : t.is_expired
                  ? 'border-status-warning text-status-warning'
                  : 'border-accent text-accent';
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-2 rounded-md border border-border bg-bg-surface px-2 py-1"
                >
                  <Badge
                    variant="outline"
                    className={cn(
                      'min-w-[80px] justify-center font-mono',
                      stateClass,
                    )}
                  >
                    {stateLabel}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm">
                      {t.label || `token #${t.id}`}
                      {t.camera_id ? ` · ${t.camera_id}` : ''}
                    </div>
                    <div className="text-xs text-fg-muted">
                      expires {fmt(t.expires_at)} · uses {t.use_count} · last {fmt(t.last_used_at)}
                    </div>
                  </div>
                  {!t.is_revoked && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => revoke(t.id)}
                            className="text-status-error"
                            aria-label="Revoke"
                          >
                            <Trash2 />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Revoke</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
